import React from 'react';
import { Button } from '../ui/button';
import { tiktym } from '../../data/mock';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Mic,
  Timer,
  CalendarClock,
  ShieldCheck,
  TrendingUp,
  WifiOff
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/use-scroll-animation';

const featureIcons = {
  Mic,
  Timer,
  CalendarClock,
  ShieldCheck,
  TrendingUp,
  WifiOff
};

const BEAD_COUNT = 108;
const DEMO_MINUTES = 64;
const DAILY_TARGET = 100;

// Ring of 108 beads, echoing TikTym's daily progress view
const BeadRing = () => {
  const filled = Math.round((DEMO_MINUTES / DAILY_TARGET) * BEAD_COUNT);
  const radius = 130;
  const center = 160;

  return (
    <svg viewBox="0 0 320 320" className="w-full h-full" role="img" aria-label={`${DEMO_MINUTES} of ${DAILY_TARGET} minutes logged today`}>
      {Array.from({ length: BEAD_COUNT }).map((_, i) => {
        const angle = (i / BEAD_COUNT) * 2 * Math.PI - Math.PI / 2;
        return (
          <circle
            key={i}
            cx={center + radius * Math.cos(angle)}
            cy={center + radius * Math.sin(angle)}
            r={3.4}
            className={i < filled ? 'fill-amber-500' : 'fill-slate-200'}
          />
        );
      })}
    </svg>
  );
};

const FeatureCard = ({ feature, index }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });
  const IconComponent = featureIcons[feature.icon] || Timer;

  return (
    <div
      ref={ref}
      className={`group flex gap-4 p-5 rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-sm hover:bg-white hover:shadow-premium-lg hover:border-slate-300 transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center group-hover:from-slate-900 group-hover:to-slate-800 transition-all duration-500">
        <IconComponent className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors duration-500" />
      </div>
      <div>
        <h4 className="font-bold text-slate-900 text-sm mb-1">{feature.title}</h4>
        <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
      </div>
    </div>
  );
};

export const TikTymSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [visualRef, visualVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="tiktym" className="py-32 lg:py-40 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-br from-amber-100/30 via-slate-100/30 to-transparent rounded-full blur-3xl opacity-60 animate-float-slow pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Copy */}
          <div
            ref={headerRef}
            className={`transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-bold text-slate-500 uppercase tracking-widest">Our Product</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-100 to-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Live
              </span>
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              {tiktym.name}
            </h2>
            <p className="mt-4 text-2xl sm:text-3xl font-bold text-slate-600 tracking-tight leading-snug">
              {tiktym.tagline}
            </p>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed font-medium">
              {tiktym.description}
            </p>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
              {tiktym.highlights.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white px-8 py-6 text-base font-semibold group shadow-premium-lg hover:shadow-premium-xl transition-all duration-300 hover:scale-105"
              >
                <a href={tiktym.appUrl} target="_blank" rel="noopener noreferrer">
                  Try TikTym Free
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 px-8 py-6 text-base font-semibold group shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm"
              >
                <a href={tiktym.websiteUrl} target="_blank" rel="noopener noreferrer">
                  Visit tiktym.com
                  <ArrowUpRight className="ml-1 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </a>
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-500 font-medium">
              Web app for iPhone, Android and laptop. Add it to your home screen, no app store needed.
            </p>
          </div>

          {/* Visual */}
          <div
            ref={visualRef}
            className={`relative mx-auto w-full max-w-md transition-all duration-700 delay-150 ${
              visualVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="relative aspect-square rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-white border border-slate-200/80 shadow-premium-xl p-8">
              <BeadRing />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Today</span>
                <span className="mt-2 text-6xl font-extrabold text-slate-900 tracking-tight">{DEMO_MINUTES}</span>
                <span className="mt-1 text-sm font-semibold text-slate-500">of {DAILY_TARGET} minutes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tiktym.features.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
